import { NSARouterMakerResult } from "namirasoft-account-react";
import { FilterItem, FilterItemOperator, NamingConvention } from "namirasoft-core";
import { EnvironmentRow, KubernetesRow, MetricCallerType, MetricInputRow, MetricRow, MetricType, ProjectRow, ServerRow } from "namirasoft-infra";
import { LogGroupRow } from "namirasoft-log";
import { SchedulerRow } from "namirasoft-scheduler";
import { NamirasoftMap } from "namirasoft-site-map";
import { NSBox, NSBoxEntity, NSBoxEnum, NSBoxString, NSBoxTextArea, NSBoxURL, NSColumn } from "namirasoft-site-react";
import { Component, createRef } from "react";
import { InfraEntities } from "../InfraEntities";
import { InfraLayout } from '../InfraLayout';
import { InfraRouterProps } from "../InfraRouterProps";

interface MetricEditPageProps extends NSARouterMakerResult, InfraRouterProps
{
    isEdit: boolean;
}

interface MetricEditPageState
{
    selected_project_id?: string;
    selected_environment_id?: string;
    caller_type?: MetricCallerType;
}

export default class MetricEditPage extends Component<MetricEditPageProps, MetricEditPageState>
{
    NSBoxString_Name = createRef<NSBoxString>();
    NSBoxEnum_Type = createRef<NSBoxEnum<MetricType>>();
    NSBoxEntity_ProjectID = createRef<NSBoxEntity<ProjectRow>>();
    NSBoxEntity_EnvironmentID = createRef<NSBoxEntity<EnvironmentRow>>();
    NSBoxEntity_LogGroupID = createRef<NSBoxEntity<LogGroupRow>>();
    NSBoxEnum_CallerType = createRef<NSBoxEnum<MetricCallerType>>();
    NSBoxEntity_CallerServerID = createRef<NSBoxEntity<ServerRow>>();
    NSBoxEntity_CallerKubernetesID = createRef<NSBoxEntity<KubernetesRow>>();
    NSBoxURL_Url = createRef<NSBoxURL>();
    NSBoxEntity_IntervalSchedulerID = createRef<NSBoxEntity<SchedulerRow>>();
    NSBoxTextArea_Description = createRef<NSBoxTextArea>();

    constructor(props: MetricEditPageProps)
    {
        super(props);
        this.state = {};
    }

    override render()
    {
        let namirasoft = new NamirasoftMap(this.props.account.token_manager, console.error);

        return (
            <InfraLayout<MetricRow, MetricInputRow>
                {...this.props}
                pages={{
                    entity: InfraEntities.metric(this.props),
                    edit: {
                        ...this.props,
                        ui: {
                            tab_page_edit: {
                                editor: {
                                    getInputRow: (): MetricInputRow =>
                                    {
                                        let name = this.NSBoxString_Name.current?.getValue() ?? "";
                                        let type = this.NSBoxEnum_Type.current?.getValueEnumOne(MetricType, MetricType.Prometheus) ?? MetricType.Prometheus;
                                        let project_id = this.NSBoxEntity_ProjectID.current?.getValueOne() ?? "";
                                        let environment_id = this.NSBoxEntity_EnvironmentID.current?.getValueOne() ?? "";
                                        let log_group_id = this.NSBoxEntity_LogGroupID.current?.getValueOne() ?? null;
                                        let caller_type = this.NSBoxEnum_CallerType.current?.getValueEnumOne(MetricCallerType, MetricCallerType.Direct) ?? MetricCallerType.Direct;
                                        let caller_server_id = this.NSBoxEntity_CallerServerID.current?.getValueOne(this.state.caller_type === MetricCallerType.ByServer) ?? null;
                                        let caller_kubernetes_id = this.NSBoxEntity_CallerKubernetesID.current?.getValueOne(this.state.caller_type === MetricCallerType.ByKubernetes) ?? null;
                                        let url = this.NSBoxURL_Url.current?.getValue() ?? "";
                                        let interval_scheduler_id = this.NSBoxEntity_IntervalSchedulerID.current?.getValueOne() ?? "";
                                        let description = this.NSBoxTextArea_Description.current?.getValue() ?? "";

                                        return {
                                            workspace_id: "",
                                            project_id,
                                            environment_id,
                                            log_group_id,
                                            name,
                                            type,
                                            caller_type,
                                            caller_server_id,
                                            caller_kubernetes_id,
                                            url,
                                            interval_scheduler_id,
                                            description,
                                            metric_category: [],
                                            metric_field: [],
                                            metric_tag: [],
                                        };
                                    },
                                    setRow: (row: MetricRow) =>
                                    {
                                        this.NSBoxString_Name.current?.setValue(row.name);
                                        this.NSBoxEnum_Type.current?.setValueOne(row.type);
                                        this.NSBoxEntity_ProjectID.current?.setValueOne(row.project_id);
                                        this.NSBoxEntity_LogGroupID.current?.setValueOne(row.log_group_id);
                                        this.NSBoxEnum_CallerType.current?.setValueOne(row.caller_type);
                                        this.NSBoxURL_Url.current?.setValue(row.url);
                                        this.NSBoxEntity_IntervalSchedulerID.current?.setValueOne(row.interval_scheduler_id);
                                        this.NSBoxTextArea_Description.current?.setValue(row.description);

                                        this.setState(
                                            () => ({
                                                selected_project_id: row.project_id,
                                                selected_environment_id: row.environment_id,
                                                caller_type: row.caller_type
                                            }),
                                            () =>
                                            {
                                                this.NSBoxEntity_EnvironmentID.current?.setValueOne(row.environment_id);
                                                this.NSBoxEntity_CallerServerID.current?.setValueOne(row.caller_server_id);
                                                this.NSBoxEntity_CallerKubernetesID.current?.setValueOne(row.caller_kubernetes_id);
                                            }
                                        );
                                    }
                                },
                                ui: {
                                    extra_tabs: [
                                        {
                                            title: "Caller",
                                            clicked: true,
                                            getContent: () => (
                                                <NSColumn>
                                                    <NSBoxEnum<MetricCallerType>
                                                        ref={this.NSBoxEnum_CallerType}
                                                        title="Type"
                                                        required
                                                        multiple={false}
                                                        getEnumObject={() => MetricCallerType}
                                                        getTitle={(item) => NamingConvention.auto.convert(item, NamingConvention.Pascal_Case_Space)}
                                                        onChanged={(box) => this.setState(() => ({ caller_type: box.getValueOne() as MetricCallerType }))}
                                                        style={{ width: NSBox.width.double }}
                                                    />
                                                    {
                                                        this.state.caller_type === MetricCallerType.Direct &&
                                                        <img
                                                            style={{ width: "100%", maxWidth: NSBox.width.double }}
                                                            src="https://static.namirasoft.com/image/namirasoft/infra/diagram/metric/connect-type/direct.png"
                                                            alt="Direct"
                                                        />
                                                    }
                                                    {
                                                        this.state.caller_type === MetricCallerType.ByServer &&
                                                        <>
                                                            <NSBoxEntity<ServerRow>
                                                                ref={this.NSBoxEntity_CallerServerID}
                                                                title="Server"
                                                                required
                                                                multiple={false}
                                                                table_name="server"
                                                                list={async (filters, page, size, sorts) =>
                                                                {
                                                                    if (!filters)
                                                                        filters = [];

                                                                    if (this.state.selected_environment_id)
                                                                        filters.push(new FilterItem(
                                                                            namirasoft.infra.tables.server,
                                                                            namirasoft.infra.tables.server.columns.environment_id,
                                                                            false,
                                                                            FilterItemOperator.all.equals,
                                                                            this.state.selected_environment_id,
                                                                        ));

                                                                    return await this.props.servers.infra.server.List(filters, page, size, sorts, null);
                                                                }}
                                                                getValue={(item) => item.id}
                                                                getTitle={(item) => item.name}
                                                                getSort={() => null}
                                                                style={{ width: NSBox.width.double }}
                                                            />
                                                            <img
                                                                style={{ width: "100%", maxWidth: NSBox.width.double }}
                                                                src="https://static.namirasoft.com/image/namirasoft/infra/diagram/metric/connect-type/by-server.png"
                                                                alt="By Server"
                                                            />
                                                        </>
                                                    }
                                                    {
                                                        this.state.caller_type === MetricCallerType.ByKubernetes &&
                                                        <>
                                                            <NSBoxEntity<KubernetesRow>
                                                                ref={this.NSBoxEntity_CallerKubernetesID}
                                                                title="Kubernetes"
                                                                required
                                                                multiple={false}
                                                                table_name="kubernetes"
                                                                list={async (filters, page, size, sort, all_workspaces) =>
                                                                {
                                                                    if (!filters)
                                                                        filters = [];

                                                                    filters.push(new FilterItem(
                                                                        namirasoft.infra.tables.kubernetes,
                                                                        namirasoft.infra.tables.kubernetes.columns.agent_pod_installation_enabled,
                                                                        false,
                                                                        FilterItemOperator.all.equals,
                                                                        "1",
                                                                    ));

                                                                    if (this.state.selected_environment_id)
                                                                        filters.push(new FilterItem(
                                                                            namirasoft.infra.tables.kubernetes,
                                                                            namirasoft.infra.tables.kubernetes.columns.environment_id,
                                                                            false,
                                                                            FilterItemOperator.all.equals,
                                                                            this.state.selected_environment_id,
                                                                        ));

                                                                    return await this.props.servers.infra.kubernetes.List(filters, page, size, sort, all_workspaces);
                                                                }}
                                                                getValue={(item) => item.id}
                                                                getTitle={(item) => item.name}
                                                                getSort={() => null}
                                                                style={{ width: NSBox.width.double }}
                                                            />
                                                            <img
                                                                style={{ width: "100%", maxWidth: NSBox.width.double }}
                                                                src="https://static.namirasoft.com/image/namirasoft/infra/diagram/metric/connect-type/by-kubernetes.png"
                                                                alt="By Kubernetes"
                                                            />
                                                        </>
                                                    }
                                                </NSColumn>
                                            )
                                        },
                                    ]
                                }
                            }
                        }
                    }
                }}
            >
                <NSColumn>
                    <NSBoxString
                        ref={this.NSBoxString_Name}
                        required
                        title="Name"
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxEnum<MetricType>
                        ref={this.NSBoxEnum_Type}
                        title="Type"
                        required
                        multiple={false}
                        getEnumObject={() => MetricType}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxEntity<ProjectRow>
                        ref={this.NSBoxEntity_ProjectID}
                        title="Project"
                        required
                        multiple={false}
                        table_name="project"
                        table={namirasoft.infra.tables.project}
                        getValue={(item) => item.id}
                        getTitle={(item) => item.name}
                        getSort={() => null}
                        onChanged={(box) =>
                        {
                            this.setState(
                                () => ({ selected_project_id: box.getValueOne() ?? undefined }),
                                () =>
                                {
                                    this.NSBoxEntity_EnvironmentID.current?.setValue(null);
                                    this.NSBoxEntity_EnvironmentID.current?.reload();
                                },
                            )
                        }}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxEntity<EnvironmentRow>
                        ref={this.NSBoxEntity_EnvironmentID}
                        title="Environment"
                        required
                        multiple={false}
                        table_name="environment"
                        list={async (filters, page, size, sorts) =>
                        {
                            if (!filters)
                                filters = [];

                            if (this.state.selected_project_id)
                                filters.push(new FilterItem(
                                    namirasoft.infra.tables.environment,
                                    namirasoft.infra.tables.environment.columns.project_id,
                                    false,
                                    FilterItemOperator.all.equals,
                                    this.state.selected_project_id,
                                ));

                            return await this.props.servers.infra.environment.List(filters, page, size, sorts, null);
                        }}
                        getValue={(item) => item.id}
                        getTitle={(item) => item.name}
                        getSort={() => null}
                        onChanged={(box) =>
                        {
                            this.setState(
                                () => ({ selected_environment_id: box.getValueOne(false) ?? "" }),
                                () =>
                                {
                                    this.NSBoxEntity_CallerServerID.current?.setValue(null);
                                    this.NSBoxEntity_CallerKubernetesID.current?.setValue(null);
                                    this.NSBoxEntity_CallerServerID.current?.reload();
                                    this.NSBoxEntity_CallerKubernetesID.current?.reload();
                                },
                            )
                        }}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxEntity<LogGroupRow>
                        ref={this.NSBoxEntity_LogGroupID}
                        title="Log Group"
                        required={false}
                        multiple={false}
                        table_name="log-group"
                        table={namirasoft.log.tables.log_group}
                        getValue={(item) => item.id}
                        getTitle={(item) => item.name}
                        getSort={() => null}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxURL
                        ref={this.NSBoxURL_Url}
                        required
                        title="URL"
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxEntity<SchedulerRow>
                        ref={this.NSBoxEntity_IntervalSchedulerID}
                        required
                        title="Interval Scheduler"
                        multiple={false}
                        table_name="scheduler"
                        table={namirasoft.scheduler.tables.scheduler}
                        getValue={(item) => item.id}
                        getTitle={(item) => item.name}
                        getSort={() => null}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxTextArea
                        ref={this.NSBoxTextArea_Description}
                        required
                        title="Description"
                        style={{ width: NSBox.width.double }}
                        min_length={50}
                    />
                </NSColumn>
            </InfraLayout>
        );
    }
}